<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';

import T from './CompanyProfilePayTab.i18n.js';

import { getStandardLineChartConfig } from 'JIX/highcharts/configs/line_chart.js';
import { getStandardColumnChartConfig } from 'JIX/highcharts/configs/column_chart.js';

const store = useStore();

const companyName = computed(() => store.state.companyName);
const companyInfo = computed(() => store.state.companyInfo);

const averagePayChartOptions = computed(() => {
    const payComparisons = store.state.topComparisons.map(e => ({ y: e[1], name: e[0] }));
    payComparisons.push({ name: companyName.value, y: companyInfo.value.average_wage, color: '#7d899B' });
    payComparisons.sort((a, b) => a.y - b.y);

    return getStandardColumnChartConfig({
        chart: {
            borderWidth: 0,
        },
        title: {
            text: null,
        },
        xAxis: {
            allowDecimals: false,
            categories: payComparisons.map(e => e.name),
        },
        yAxis: {
            title: {
                text: T.avgPayYAxis()
            },
        },
        plotOptions: {
            series: {
                animation: false,
            },
        },
        series: [
            {
                data: payComparisons,
                name: T.avgPayYAxis(),
            },
        ],
    });
});

const reportChartOptions = computed(() => {
    const currency = companyInfo.value.latest_currency;
    return getStandardLineChartConfig({
        chart: {
            borderWidth: 0
        },
        title: {
            text: null,
        },
        xAxis: {
            allowDecimals: false,
        },
        yAxis: {
            title: {
                text: currency
            },
        },
        tooltip: {
            valueSuffix: ' ' + currency
        },
        plotOptions: {
            series: {
                animation: false,
            },
        },
        series: [
            {
                name: T.reportChartRevenue(),
                data: companyInfo.value.accounts.map(x => [parseInt(x.year, 10), parseInt(x.revenue, 10) * 1000]),
            },
            {
                name: T.reportChartIncome(),
                data: companyInfo.value.accounts.map(x => [parseInt(x.year, 10), parseInt(x.gross_profit, 10) * 1000]),
            },
            {
                name: T.reportChartResult(),
                data: companyInfo.value.accounts.map(x => [parseInt(x.year, 10), parseInt(x.profit_of_the_year, 10) * 1000]),
            },
        ],
    });
});

function jixAnalytics(label, action) {
    store.dispatch('jixAnalytics', { label, action });
}
</script>

<template>
    <div>
        <div v-if="store.state.enableReport">
            <h3>
                {{ T.reportHeader(companyName) }}
            </h3>
            <p class="mb-4">
                {{ T.reportExplain(companyName) }}
            </p>
            <div class="bg-surface-default border p-2 p-md-4 mb-4">
                <h4>{{ T.reportChartTitle() }}</h4>
                <Highcharts :options="reportChartOptions" />
            </div>

            <div v-html="store.state.reportTable" />
        </div>

        <div v-if="store.state.enableAvgWage" class="mt-4">
            <h3>
                {{ T.avgPayHeader() }}
            </h3>
            <div class="mb-3 bg-surface-default border p-2 p-md-4">
                <div class="row">
                    <div class="col-md-8">
                        <h4>
                            {{ T.avgPayChartTitle(companyName) }}
                        </h4>
                        <Highcharts :options="averagePayChartOptions" />
                    </div>
                    <div class="col-md-4">
                        <h4>
                            {{ T.avgPayDefinitionHeader() }}
                        </h4>
                        <p>
                            {{ T.avgPayDefinition() }}
                            <a
                                @click="jixAnalytics('om-gennemsnitsløn','klik-menu-løn-regnskab')"
                                href=""
                                data-toggle="modal"
                                data-target="#avg-pay-explain-modal"
                            >
                                {{ T.readMore() }}
                            </a>
                        </p>
                    </div>
                </div>
                <div id="avg-pay-explain-modal" class="modal fade" role="dialog">
                    <div class="modal-dialog" role="document">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h3>
                                    {{ T.avgPayModalHeader() }}
                                </h3>
                                <button
                                    type="button"
                                    class="close"
                                    data-dismiss="modal"
                                    aria-label="Close"
                                >
                                    <span aria-hidden="true">x</span>
                                </button>
                            </div>
                            <div class="modal-body">
                                <b>
                                    {{ T.avgPayModalDataHeader() }}
                                </b>
                                <p>
                                    {{ T.avgPayModalDataExplain() }}
                                </p>
                                <b>
                                    {{ T.avgPayModalComparisonHeader() }}
                                </b>
                                <p>
                                    {{ T.avgPayModalComparisonExplain() }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
